/**
 * MTG League Scoring System
 *
 * Scoring Rules:
 * - Participation: +1 point for playing any event round (once per event per player)
 * - Match Result: Win +3, Draw +1, Loss +0
 * - Top 8 Bonus: 1st +10, 2nd +6, 3rd-4th +3, 5th-8th +1
 */

export interface LeagueScoringConfig {
  participationPoints: number;
  winPoints: number;
  drawPoints: number;
  lossPoints: number;
  top8FirstPoints: number;
  top8SecondPoints: number;
  top8ThirdFourthPoints: number;
  top8FifthEighthPoints: number;
  commanderFirstPoints: number;
  commanderSecondPoints: number;
  commanderThirdPoints: number;
  commanderFourthPoints: number;
  premiumMultiplier: number;
}

export const DEFAULT_LEAGUE_SCORING: LeagueScoringConfig = {
  participationPoints: 1,
  winPoints: 3,
  drawPoints: 1,
  lossPoints: 0,
  top8FirstPoints: 10,
  top8SecondPoints: 6,
  top8ThirdFourthPoints: 3,
  top8FifthEighthPoints: 1,
  commanderFirstPoints: 4,
  commanderSecondPoints: 3,
  commanderThirdPoints: 2,
  commanderFourthPoints: 1,
  premiumMultiplier: 2,
};

type LeagueScoringSource = {
  [Key in keyof LeagueScoringConfig]?: number | null;
};

export function getLeagueScoring(
  league?: LeagueScoringSource | null
): LeagueScoringConfig {
  return Object.fromEntries(
    Object.entries(DEFAULT_LEAGUE_SCORING).map(([key, defaultValue]) => [
      key,
      typeof league?.[key as keyof LeagueScoringConfig] === "number"
        ? league[key as keyof LeagueScoringConfig]
        : defaultValue,
    ])
  ) as unknown as LeagueScoringConfig;
}

export interface MatchResult {
  playerId: string;
  opponentId: string;
  result: "win" | "loss" | "draw";
  eventId: string;
  round: number;
}

export interface Top8Placement {
  playerId: string;
  placement: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
  eventId: string;
}

export interface PlayerScore {
  playerId: string;
  participationPoints: number;
  matchPoints: number;
  top8Points: number;
  totalPoints: number;
  eventsPlayed: number;
  matchesPlayed: number;
  wins: number;
  draws: number;
  losses: number;
}

export interface EventParticipation {
  playerId: string;
  eventId: string;
  roundsPlayed: number;
}

/**
 * Calculate match points based on result
 */
export function calculateMatchPoints(
  result: "win" | "loss" | "draw",
  scoring: LeagueScoringConfig = DEFAULT_LEAGUE_SCORING
): number {
  switch (result) {
    case "win":
      return scoring.winPoints;
    case "draw":
      return scoring.drawPoints;
    case "loss":
      return scoring.lossPoints;
    default:
      return 0;
  }
}

/**
 * Calculate Top 8 bonus points based on placement
 */
export function calculateTop8Points(
  placement: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8,
  scoring: LeagueScoringConfig = DEFAULT_LEAGUE_SCORING
): number {
  switch (placement) {
    case 1:
      return scoring.top8FirstPoints;
    case 2:
      return scoring.top8SecondPoints;
    case 3:
    case 4:
      return scoring.top8ThirdFourthPoints;
    case 5:
    case 6:
    case 7:
    case 8:
      return scoring.top8FifthEighthPoints;
    default:
      return 0;
  }
}

export function calculateCommanderPlacementPoints(
  placement: 1 | 2 | 3 | 4,
  scoring: LeagueScoringConfig = DEFAULT_LEAGUE_SCORING
): number {
  switch (placement) {
    case 1:
      return scoring.commanderFirstPoints;
    case 2:
      return scoring.commanderSecondPoints;
    case 3:
      return scoring.commanderThirdPoints;
    case 4:
      return scoring.commanderFourthPoints;
    default:
      return 0;
  }
}

/**
 * Calculate participation points (1 point per event)
 */
export function calculateParticipationPoints(
  eventsPlayed: number,
  scoring: LeagueScoringConfig = DEFAULT_LEAGUE_SCORING
): number {
  return eventsPlayed * scoring.participationPoints;
}

export function calculateRecordPoints(
  wins: number,
  draws: number,
  losses: number,
  scoring: LeagueScoringConfig = DEFAULT_LEAGUE_SCORING
): number {
  return (
    wins * scoring.winPoints +
    draws * scoring.drawPoints +
    losses * scoring.lossPoints
  );
}

export function applyPremiumMultiplier(
  points: number,
  isPremium: boolean,
  scoring: LeagueScoringConfig = DEFAULT_LEAGUE_SCORING
): number {
  return isPremium ? points * scoring.premiumMultiplier : points;
}

/**
 * Calculate total player score from all components
 */
export function calculateTotalScore(
  participationPoints: number,
  matchPoints: number,
  top8Points: number
): number {
  return participationPoints + matchPoints + top8Points;
}

/**
 * Aggregate match results for a player
 */
export function aggregateMatchResults(
  matches: MatchResult[],
  playerId: string
) {
  const playerMatches = matches.filter((m) => m.playerId === playerId);

  return {
    matchesPlayed: playerMatches.length,
    wins: playerMatches.filter((m) => m.result === "win").length,
    draws: playerMatches.filter((m) => m.result === "draw").length,
    losses: playerMatches.filter((m) => m.result === "loss").length,
    matchPoints: playerMatches.reduce(
      (sum, match) => sum + calculateMatchPoints(match.result),
      0
    ),
  };
}

/**
 * Calculate Top 8 points for a player
 */
export function calculatePlayerTop8Points(
  placements: Top8Placement[],
  playerId: string
): number {
  return placements
    .filter((p) => p.playerId === playerId)
    .reduce(
      (sum, placement) => sum + calculateTop8Points(placement.placement),
      0
    );
}

/**
 * Calculate participation points for a player
 */
export function calculatePlayerParticipationPoints(
  participations: EventParticipation[],
  playerId: string
): number {
  const playerEvents = participations.filter((p) => p.playerId === playerId);
  return playerEvents.length;
}

/**
 * Calculate complete player score
 */
export function calculatePlayerScore(
  playerId: string,
  matches: MatchResult[],
  placements: Top8Placement[],
  participations: EventParticipation[]
): PlayerScore {
  const matchStats = aggregateMatchResults(matches, playerId);
  const top8Points = calculatePlayerTop8Points(placements, playerId);
  const participationPoints = calculatePlayerParticipationPoints(
    participations,
    playerId
  );
  const totalPoints = calculateTotalScore(
    participationPoints,
    matchStats.matchPoints,
    top8Points
  );

  return {
    playerId,
    participationPoints,
    matchPoints: matchStats.matchPoints,
    top8Points,
    totalPoints,
    eventsPlayed: participationPoints,
    matchesPlayed: matchStats.matchesPlayed,
    wins: matchStats.wins,
    draws: matchStats.draws,
    losses: matchStats.losses,
  };
}

/**
 * Calculate standings for all players in a league
 */
export function calculateStandings(
  playerIds: string[],
  matches: MatchResult[],
  placements: Top8Placement[],
  participations: EventParticipation[]
): PlayerScore[] {
  return playerIds
    .map((playerId) =>
      calculatePlayerScore(playerId, matches, placements, participations)
    )
    .sort((a, b) => {
      // Sort by total points (descending)
      if (b.totalPoints !== a.totalPoints) {
        return b.totalPoints - a.totalPoints;
      }
      // Tiebreaker: match points
      if (b.matchPoints !== a.matchPoints) {
        return b.matchPoints - a.matchPoints;
      }
      // Tiebreaker: wins
      if (b.wins !== a.wins) {
        return b.wins - a.wins;
      }
      // Tiebreaker: events played (more events = higher rank)
      return b.eventsPlayed - a.eventsPlayed;
    });
}

/**
 * Validate match result data
 */
export function validateMatchResult(match: MatchResult): boolean {
  return !!(
    match.playerId &&
    match.opponentId &&
    match.playerId !== match.opponentId &&
    ["win", "loss", "draw"].includes(match.result) &&
    match.eventId &&
    match.round > 0
  );
}

/**
 * Validate Top 8 placement data
 */
export function validateTop8Placement(placement: Top8Placement): boolean {
  return !!(
    placement.playerId &&
    placement.eventId &&
    placement.placement >= 1 &&
    placement.placement <= 8
  );
}

/**
 * Get unique events from participations
 */
export function getUniqueEvents(
  participations: EventParticipation[]
): string[] {
  return Array.from(new Set(participations.map((p) => p.eventId)));
}

/**
 * Get players who participated in a specific event
 */
export function getEventParticipants(
  participations: EventParticipation[],
  eventId: string
): string[] {
  return participations
    .filter((p) => p.eventId === eventId)
    .map((p) => p.playerId);
}
